import {
    DataGranularity,
    MeterDataDto,
    meterDataPaths,
    MeterReadingTypeEnum,
    SanityWarning,
} from "@flexidao/dto";
import {
    addToDate,
    getLocalStartOfDayISOString,
    ONE_MINUTE_MS,
    voidDecoder,
} from "@flexidao/helpers";
import { ApiClient } from "../api-client";
import {
    activeSitesKpiDecoder,
    alertCommentDecoder,
    alertCommentListDecoder,
    alertDecoder,
    basicMeterArrayDecoder,
    biddingZoneOptionArrayDecoder,
    consumptionBySiteDecoder,
    dataCompletenessDecoder,
    getAlertsKpisResponseDecoder,
    getAlertsResponseDecoder,
    getIntervalMetersFiltersResponseDecoder,
    getIntervalMetersResponseDecoder,
    getMonthlyGridMetersFiltersResponseDecoder,
    getMonthlyGridMetersResponseDecoder,
    gridConsumptionComparisonDecoder,
    gridConsumptionKpisDecoder,
    gridConsumptionMonthlyBreakdownDecoder,
    hourlyAggregatedMeterDataArrayDecoder,
    hourlyAggregatedWarningsPerSiteArrayDecoder,
    hourlyMeterWarningsArrayDecoder,
    hourlySiteWarningsArrayDecoder,
    meterDataSiteBreakdownDecoder,
    meterOptionArrayDecoder,
    monitoringSiteDetailsKpisDecoder,
    providerOptionArrayDecoder,
    regionOptionArrayDecoder,
    siteBriefArrayDecoder,
    siteOptionArrayDecoder,
    totalConsumptionAndProductionKpiDecoder,
    warningTypeArrayDecoder,
} from "../decoders";
import { ApiResponse, ApiResultResponse, OverloadApiResponse } from "../types";
import { buildQueries, crash } from "../utils";

const formatStartTimeLocal = (date: Date): string => getLocalStartOfDayISOString(date);

const formatEndTimeLocal = (date: Date): string =>
    addToDate(new Date(getLocalStartOfDayISOString(date)), { day: 1 }).toISOString();

type GetAccessToken = () => Promise<string>;

type PutSitesBySiteIdMetersByTenantId = {
    getAccessToken: GetAccessToken;
    tenantId: string;
    siteId: string;
    meterIds: Array<number>;
};

type GetAvailableMetersByTenantId = {
    getAccessToken: GetAccessToken;
    tenantId: string;
    siteId?: string;
};

export type GetMeterDataByTenantIdSiteBreakdownArgs<T> = {
    getAccessToken: GetAccessToken;
    tenantId: string;
    startTime: Date;
    endTime: Date;
    regionIds: Array<string> | null;
    biddingZoneIds: Array<string> | null;
    siteIds: Array<string> | null;
    page: number;
    pageSize: number;
    sorting?: {
        columnId: Extract<keyof T, string>;
        direction: "asc" | "desc";
    } | null;
    filters?: Record<string, string | null | Array<string | null>> | null;
};

export type GetActiveSitesKpiByTenantIdArgs = {
    getAccessToken: GetAccessToken;
    tenantId: string;
    startTime: Date;
    endTime: Date;
    regionIds: Array<string> | null;
    biddingZoneIds: Array<string> | null;
    siteIds: Array<string> | null;
};

export type GetTotalConsumptionAndProductionKpiByTenantIdArgs = {
    getAccessToken: GetAccessToken;
    tenantId: string;
    startTime: Date;
    endTime: Date;
    regionIds: Array<string> | null;
    biddingZoneIds: Array<string> | null;
    siteIds: Array<string> | null;
};

export type GetHourlyAggregatedMeterDataByTenantIdArgs = {
    getAccessToken: GetAccessToken;
    tenantId: string;
    startTime: Date;
    endTime: Date;
    regionIds: Array<string> | null;
    biddingZoneIds: Array<string> | null;
    siteIds: Array<string> | null;
    granularity: DataGranularity | null;
};

export type GetDataCompletenessByTenantIdArgs = {
    getAccessToken: GetAccessToken;
    tenantId: string;
    startTime: Date;
    endTime: Date;
    regionIds: Array<string> | null;
    countryIds: Array<string> | null;
    biddingZoneIds: Array<string> | null;
    siteIds: Array<string> | null;
};

export type GetAggregatedWarningsPerSiteByTenantIdArgs = {
    getAccessToken: GetAccessToken;
    tenantId: string;
    startTime: Date;
    endTime: Date;
    siteIds: Array<string>;
};

export type GetRegionsByTenantIdArgs = {
    getAccessToken: GetAccessToken;
    tenantId: string;
    startTime: Date;
    endTime: Date;
};

export type GetBiddingZonesByTenantIdArgs = {
    getAccessToken: GetAccessToken;
    tenantId: string;
    startTime: Date;
    endTime: Date;
    regionIds?: Array<string>;
};

export type GetSitesByTenantIdArgs = {
    getAccessToken: GetAccessToken;
    tenantId: string;
    startTime: Date;
    endTime: Date;
    regionIds?: Array<string>;
    biddingZoneIds?: Array<string>;
};

export type GetProvidersByTenantIdArgs = {
    getAccessToken: GetAccessToken;
    tenantId: string;
    startTime: Date;
    endTime: Date;
    regionIds?: Array<string>;
    biddingZoneIds?: Array<string>;
    siteIds?: Array<string>;
};

export type GetConsumptionBySiteByTenantIdArgs = {
    getAccessToken: GetAccessToken;
    tenantId: string;
    startTimeLocal: Date;
    endTimeLocal: Date;
    page: number;
    regionIds?: Array<string>;
    consumptionSiteGroupIds?: Array<string>;
    orderBy?: "consumptionWh" | "siteName";
    orderDir?: "asc" | "desc";
    readingTypes?: Array<MeterReadingTypeEnum>;
};

export type GetIntervalMetersByTenantIdArgs = {
    getAccessToken: GetAccessToken;
} & MeterDataDto.GetIntervalMetersPayload;

export type GetIntervalMetersFiltersByTenantIdArgs = {
    getAccessToken: GetAccessToken;
} & MeterDataDto.GetIntervalMetersFiltersPayload;

export type GetMonthlyGridMetersByTenantIdArgs = {
    getAccessToken: GetAccessToken;
} & MeterDataDto.GetMonthlyGridMetersPayload;

export type GetMonthlyGridMetersFiltersByTenantIdArgs = {
    getAccessToken: GetAccessToken;
} & MeterDataDto.GetMonthlyGridMetersFiltersPayload;

export type GetWarningTypeOptionsByTenantIdArgs = {
    getAccessToken: GetAccessToken;
} & MeterDataDto.GetWarningTypeOptionsPayload;

export type GetMeterOptionsByTenantIdArgs = {
    getAccessToken: GetAccessToken;
} & MeterDataDto.GetMeterOptionsPayload;

export type GetMonitoringSiteDetailsKpisByTenantIdArgs = {
    getAccessToken: GetAccessToken;
} & MeterDataDto.GetMonitoringSiteDetailsKpisByTenantIdPayload;

export type GetMonitoringSiteDetailsKpisByTenantIdResponse = MeterDataDto.MonitoringSiteDetailsKpis;

export type GetHourlyMeterWarningsByTenantIdArgs = {
    getAccessToken: GetAccessToken;
} & MeterDataDto.GetHourlyMeterWarningsPayload;

export type GetHourlySiteWarningsByTenantIdArgs = {
    getAccessToken: GetAccessToken;
} & MeterDataDto.GetHourlySiteWarningsPayload;

export type GetInvoicedGridConsumptionByDataSubTypeKpisByTenantIdArgs = {
    getAccessToken: GetAccessToken;
} & MeterDataDto.GridConsumptionKpisPayload;

export type GetInvoicedGridConsumptionByDataSubTypeMonthlyBreakdownByTenantIdArgs = {
    getAccessToken: GetAccessToken;
} & MeterDataDto.GridConsumptionMonthlyBreakdownPayload;

export type GetInvoicedGridConsumptionFiltersActiveRegionsByTenantIdArgs = {
    getAccessToken: GetAccessToken;
} & MeterDataDto.GridConsumptionFilterByRegionsPayload;

export type GetInvoicedGridConsumptionFiltersActiveSitesByTenantIdArgs = {
    getAccessToken: GetAccessToken;
} & MeterDataDto.GridConsumptionFilterBySitesPayload;

export type GetInvoicedGridConsumptionByDataSubTypeComparisonByTenantIdArgs = {
    getAccessToken: GetAccessToken;
} & MeterDataDto.GridConsumptionComparisonPayload;

export type GetAlertsByTenantIdArgs = {
    getAccessToken: GetAccessToken;
} & MeterDataDto.GetAlertsPayload;

export type GetAlertsKpisByTenantIdArgs = {
    getAccessToken: GetAccessToken;
} & MeterDataDto.GetAlertsKpisPayload;

export type GetRegionsWithAlertsByTenantIdArgs = {
    getAccessToken: GetAccessToken;
} & MeterDataDto.GetRegionsWithAlertsPayload;

export type GetSitesWithAlertsByTenantIdArgs = {
    getAccessToken: GetAccessToken;
} & MeterDataDto.GetSitesWithAlertsPayload;

export type GetAlertsByAlertIdByTenantIdArgs = {
    getAccessToken: GetAccessToken;
} & MeterDataDto.GetAlertByAlertIdPayload;

export type GetAlertsByAlertIdCommentsByTenantIdArgs = {
    getAccessToken: GetAccessToken;
} & MeterDataDto.GetAlertCommentsPayload;

export type PostAlertsByAlertIdCommentsByTenantIdArgs = {
    getAccessToken: GetAccessToken;
} & MeterDataDto.PostAlertCommentPayload;

export type PostAlertsByAlertIdStatusByTenantIdArgs = {
    getAccessToken: GetAccessToken;
} & MeterDataDto.PostAlertStatusPayload;

export type DeleteAlertsByAlertIdCommentsByCommentIdByTenantIdArgs = {
    getAccessToken: GetAccessToken;
    tenantId: string;
    alertId: string;
    commentId: number;
};

type MeterDataPaths = Pick<
    meterDataPaths,
    | "/{tenantId}/alerts"
    | "/{tenantId}/alerts/{alertId}"
    | "/{tenantId}/alerts/{alertId}/status"
    | "/{tenantId}/alerts/{alertId}/comments"
    | "/{tenantId}/alerts/{alertId}/comments/{commentId}"
    | "/{tenantId}/alerts-kpis"
    | "/{tenantId}/regions-with-alerts"
    | "/{tenantId}/sites-with-alerts"
    | "/{tenantId}/interval-meters"
    | "/{tenantId}/interval-meters-filters"
    | "/{tenantId}/monthly-grid-meters"
    | "/{tenantId}/monthly-grid-meters-filters"
    | "/{tenantId}/meters/available"
    | "/{tenantId}/sites/{siteId}/meters"
    | "/{tenantId}/sites/active-kpi"
    | "/{tenantId}/total-consumption-and-production-kpi"
    | "/{tenantId}/site-breakdown"
    | "/{tenantId}/regions"
    | "/{tenantId}/bidding-zones"
    | "/{tenantId}/sites"
    | "/{tenantId}/providers"
    | "/{tenantId}/hourly-aggregated-meter-data"
    | "/{tenantId}/data-completeness"
    | "/{tenantId}/aggregated-warnings-per-site"
    | "/{tenantId}/consumption-by-site"
    | "/{tenantId}/warning-type-options"
    | "/{tenantId}/meter-options"
    | "/{tenantId}/hourly-meter-warnings"
    | "/{tenantId}/hourly-site-warnings"
    | "/{tenantId}/invoiced-grid-consumption/filters/active-regions"
    | "/{tenantId}/invoiced-grid-consumption/filters/active-sites"
    | "/{tenantId}/invoiced-grid-consumption/{dataSubType}/monthly-breakdown"
    | "/{tenantId}/invoiced-grid-consumption/{dataSubType}/kpis"
    | "/{tenantId}/invoiced-grid-consumption/{dataSubType}/comparison"
>;

export type OverloadMeterDataClient = {
    postAlertsByAlertIdStatusByTenantId: (
        args: PostAlertsByAlertIdStatusByTenantIdArgs,
    ) => OverloadApiResponse<void>;
    postAlertsByAlertIdCommentsByTenantId: (
        args: PostAlertsByAlertIdCommentsByTenantIdArgs,
    ) => OverloadApiResponse<MeterDataDto.AlertComment>;
    deleteAlertsByAlertIdCommentsByCommentIdByTenantId: (
        args: DeleteAlertsByAlertIdCommentsByCommentIdByTenantIdArgs,
    ) => OverloadApiResponse<void>;
    getAlertsByTenantId: (
        args: GetAlertsByTenantIdArgs,
    ) => OverloadApiResponse<MeterDataDto.GetAlertsResponse>;
    getAlertsByAlertIdByTenantId: (
        args: GetAlertsByAlertIdByTenantIdArgs,
    ) => OverloadApiResponse<MeterDataDto.Alert>;
    getAlertsByAlertIdCommentsByTenantId: (
        args: GetAlertsByAlertIdCommentsByTenantIdArgs,
    ) => OverloadApiResponse<Array<MeterDataDto.AlertComment>>;
    getAlertsKpisByTenantId: (
        args: GetAlertsKpisByTenantIdArgs,
    ) => OverloadApiResponse<MeterDataDto.GetAlertsKpisResponse>;
    getRegionsWithAlertsByTenantId: (
        args: GetRegionsWithAlertsByTenantIdArgs,
    ) => OverloadApiResponse<Array<MeterDataDto.RegionOption>>;
    getSitesWithAlertsByTenantId: (
        args: GetSitesWithAlertsByTenantIdArgs,
    ) => OverloadApiResponse<Array<MeterDataDto.SiteBrief>>;
    getMetersAvailableByTenantId: (
        args: GetAvailableMetersByTenantId,
    ) => OverloadApiResponse<Array<MeterDataDto.BasicMeter>>;
    getIntervalMetersByTenantId: (
        args: GetIntervalMetersByTenantIdArgs,
    ) => OverloadApiResponse<MeterDataDto.GetIntervalMetersResponse>;
    getIntervalMetersFiltersByTenantId: (
        args: GetIntervalMetersFiltersByTenantIdArgs,
    ) => OverloadApiResponse<MeterDataDto.GetIntervalMetersFiltersResponse>;
    getMonthlyGridMetersByTenantId: (
        args: GetMonthlyGridMetersByTenantIdArgs,
    ) => OverloadApiResponse<MeterDataDto.GetMonthlyGridMetersResponse>;
    getMonthlyGridMetersFiltersByTenantId: (
        args: GetMonthlyGridMetersFiltersByTenantIdArgs,
    ) => OverloadApiResponse<MeterDataDto.GetMonthlyGridMetersFiltersResponse>;
    putSitesBySiteIdMetersByTenantId: (
        args: PutSitesBySiteIdMetersByTenantId,
    ) => OverloadApiResponse<void>;
    getSiteBreakdownByTenantId: <T>(
        args: GetMeterDataByTenantIdSiteBreakdownArgs<T>,
    ) => OverloadApiResponse<MeterDataDto.MeterDataSiteBreakdown>;
    getSitesActiveKpiByTenantId: (
        args: GetActiveSitesKpiByTenantIdArgs,
    ) => OverloadApiResponse<MeterDataDto.ActiveSitesKpi>;
    getTotalConsumptionAndProductionKpiByTenantId: (
        args: GetTotalConsumptionAndProductionKpiByTenantIdArgs,
    ) => OverloadApiResponse<MeterDataDto.TotalConsumptionAndProductionKpi>;
    getRegionsByTenantId: (
        args: GetRegionsByTenantIdArgs,
    ) => OverloadApiResponse<Array<MeterDataDto.RegionOption>>;
    getBiddingZonesByTenantId: (
        args: GetBiddingZonesByTenantIdArgs,
    ) => OverloadApiResponse<Array<MeterDataDto.BiddingZoneOption>>;
    getSitesByTenantId: (
        args: GetSitesByTenantIdArgs,
    ) => OverloadApiResponse<Array<MeterDataDto.SiteOption>>;
    getProvidersByTenantId: (
        args: GetProvidersByTenantIdArgs,
    ) => OverloadApiResponse<Array<MeterDataDto.ProviderOption>>;
    getHourlyAggregatedMeterDataByTenantId: (
        args: GetHourlyAggregatedMeterDataByTenantIdArgs,
    ) => OverloadApiResponse<Array<MeterDataDto.HourlyAggregatedMeterData>>;
    getDataCompletenessByTenantId: (
        args: GetDataCompletenessByTenantIdArgs,
    ) => OverloadApiResponse<MeterDataDto.DataCompleteness>;
    getAggregatedWarningsPerSiteByTenantId: (
        args: GetAggregatedWarningsPerSiteByTenantIdArgs,
    ) => OverloadApiResponse<Array<MeterDataDto.AggregatedWarningsPerSite>>;
    getConsumptionBySiteByTenantId: (
        args: GetConsumptionBySiteByTenantIdArgs,
    ) => OverloadApiResponse<MeterDataDto.ConsumptionBySite>;
    getWarningTypeOptionsByTenantId: (
        args: GetWarningTypeOptionsByTenantIdArgs,
    ) => OverloadApiResponse<Array<SanityWarning>>;
    getMeterOptionsByTenantId: (
        args: GetMeterOptionsByTenantIdArgs,
    ) => OverloadApiResponse<Array<MeterDataDto.MeterOption>>;
    getMonitoringSiteDetailsKpisByTenantId: (
        args: GetMonitoringSiteDetailsKpisByTenantIdArgs,
    ) => OverloadApiResponse<MeterDataDto.MonitoringSiteDetailsKpis>;
    getHourlyMeterWarningsByTenantId: (
        args: GetHourlyMeterWarningsByTenantIdArgs,
    ) => OverloadApiResponse<Array<MeterDataDto.SiteHourlyMeterWarnings>>;
    getHourlySiteWarningsByTenantId: (
        args: GetHourlySiteWarningsByTenantIdArgs,
    ) => OverloadApiResponse<Array<MeterDataDto.SiteHourlySiteWarnings>>;
} & ApiClient<MeterDataPaths>;

export type MeterDataClient = {
    postAlertsByAlertIdStatusByTenantId: (
        args: PostAlertsByAlertIdStatusByTenantIdArgs,
    ) => ApiResponse<void>;
    postAlertsByAlertIdCommentsByTenantId: (
        args: PostAlertsByAlertIdCommentsByTenantIdArgs,
    ) => ApiResponse<MeterDataDto.AlertComment>;
    deleteAlertsByAlertIdCommentsByCommentIdByTenantId: (
        args: DeleteAlertsByAlertIdCommentsByCommentIdByTenantIdArgs,
    ) => ApiResponse<void>;
    getAlertsByTenantId: (
        args: GetAlertsByTenantIdArgs,
    ) => ApiResponse<MeterDataDto.GetAlertsResponse>;
    getAlertsByAlertIdByTenantId: (
        args: GetAlertsByAlertIdByTenantIdArgs,
    ) => ApiResponse<MeterDataDto.Alert>;
    getAlertsByAlertIdCommentsByTenantId: (
        args: GetAlertsByAlertIdCommentsByTenantIdArgs,
    ) => ApiResponse<Array<MeterDataDto.AlertComment>>;
    getAlertsKpisByTenantId: (
        args: GetAlertsKpisByTenantIdArgs,
    ) => ApiResponse<MeterDataDto.GetAlertsKpisResponse>;
    getRegionsWithAlertsByTenantId: (
        args: GetRegionsWithAlertsByTenantIdArgs,
    ) => ApiResponse<Array<MeterDataDto.RegionOption>>;
    getSitesWithAlertsByTenantId: (
        args: GetSitesWithAlertsByTenantIdArgs,
    ) => ApiResponse<Array<MeterDataDto.SiteBrief>>;
    getMetersAvailableByTenantId: (
        args: GetAvailableMetersByTenantId,
    ) => ApiResponse<Array<MeterDataDto.BasicMeter>>;
    getIntervalMetersByTenantId: (
        args: GetIntervalMetersByTenantIdArgs,
    ) => ApiResponse<MeterDataDto.GetIntervalMetersResponse>;
    getIntervalMetersFiltersByTenantId: (
        args: GetIntervalMetersFiltersByTenantIdArgs,
    ) => ApiResponse<MeterDataDto.GetIntervalMetersFiltersResponse>;
    getMonthlyGridMetersByTenantId: (
        args: GetMonthlyGridMetersByTenantIdArgs,
    ) => ApiResponse<MeterDataDto.GetMonthlyGridMetersResponse>;
    getMonthlyGridMetersFiltersByTenantId: (
        args: GetMonthlyGridMetersFiltersByTenantIdArgs,
    ) => ApiResponse<MeterDataDto.GetMonthlyGridMetersFiltersResponse>;
    putSitesBySiteIdMetersByTenantId: (args: PutSitesBySiteIdMetersByTenantId) => ApiResponse<void>;
    getSiteBreakdownByTenantId: <T>(
        args: GetMeterDataByTenantIdSiteBreakdownArgs<T>,
    ) => ApiResponse<MeterDataDto.MeterDataSiteBreakdown>;
    getSitesActiveKpiByTenantId: (
        args: GetActiveSitesKpiByTenantIdArgs,
    ) => ApiResponse<MeterDataDto.ActiveSitesKpi>;
    getTotalConsumptionAndProductionKpiByTenantId: (
        args: GetTotalConsumptionAndProductionKpiByTenantIdArgs,
    ) => ApiResponse<MeterDataDto.TotalConsumptionAndProductionKpi>;
    getRegionsByTenantId: (
        args: GetRegionsByTenantIdArgs,
    ) => ApiResponse<Array<MeterDataDto.RegionOption>>;
    getBiddingZonesByTenantId: (
        args: GetBiddingZonesByTenantIdArgs,
    ) => ApiResponse<Array<MeterDataDto.BiddingZoneOption>>;
    getSitesByTenantId: (
        args: GetSitesByTenantIdArgs,
    ) => ApiResponse<Array<MeterDataDto.SiteOption>>;
    getProvidersByTenantId: (
        args: GetProvidersByTenantIdArgs,
    ) => ApiResponse<Array<MeterDataDto.ProviderOption>>;
    getHourlyAggregatedMeterDataByTenantId: (
        args: GetHourlyAggregatedMeterDataByTenantIdArgs,
    ) => ApiResponse<Array<MeterDataDto.HourlyAggregatedMeterData>>;
    getDataCompletenessByTenantId: (
        args: GetDataCompletenessByTenantIdArgs,
    ) => ApiResponse<MeterDataDto.DataCompleteness>;
    getAggregatedWarningsPerSiteByTenantId: (
        args: GetAggregatedWarningsPerSiteByTenantIdArgs,
    ) => ApiResponse<Array<MeterDataDto.AggregatedWarningsPerSite>>;
    getConsumptionBySiteByTenantId: (
        args: GetConsumptionBySiteByTenantIdArgs,
    ) => ApiResponse<MeterDataDto.ConsumptionBySite>;
    getWarningTypeOptionsByTenantId: (
        args: GetWarningTypeOptionsByTenantIdArgs,
    ) => ApiResponse<Array<SanityWarning>>;
    getMeterOptionsByTenantId: (
        args: GetMeterOptionsByTenantIdArgs,
    ) => ApiResponse<Array<MeterDataDto.MeterOption>>;
    getMonitoringSiteDetailsKpisByTenantId: (
        args: GetMonitoringSiteDetailsKpisByTenantIdArgs,
    ) => ApiResponse<MeterDataDto.MonitoringSiteDetailsKpis>;
    getHourlyMeterWarningsByTenantId: (
        args: GetHourlyMeterWarningsByTenantIdArgs,
    ) => ApiResponse<Array<MeterDataDto.SiteHourlyMeterWarnings>>;
    getHourlySiteWarningsByTenantId: (
        args: GetHourlySiteWarningsByTenantIdArgs,
    ) => ApiResponse<Array<MeterDataDto.SiteHourlySiteWarnings>>;
} & ApiClient<MeterDataPaths>;

export type ResultMeterDataClient = {
    postAlertsByAlertIdStatusByTenantId: (
        args: PostAlertsByAlertIdStatusByTenantIdArgs,
    ) => ApiResultResponse<void>;
    postAlertsByAlertIdCommentsByTenantId: (
        args: PostAlertsByAlertIdCommentsByTenantIdArgs,
    ) => ApiResultResponse<MeterDataDto.AlertComment>;
    deleteAlertsByAlertIdCommentsByCommentIdByTenantId: (
        args: DeleteAlertsByAlertIdCommentsByCommentIdByTenantIdArgs,
    ) => ApiResultResponse<void>;
    getAlertsByTenantId: (
        args: GetAlertsByTenantIdArgs,
    ) => ApiResultResponse<MeterDataDto.GetAlertsResponse>;
    getAlertsByAlertIdByTenantId: (
        args: GetAlertsByAlertIdByTenantIdArgs,
    ) => ApiResultResponse<MeterDataDto.Alert>;
    getAlertsByAlertIdCommentsByTenantId: (
        args: GetAlertsByAlertIdCommentsByTenantIdArgs,
    ) => ApiResultResponse<Array<MeterDataDto.AlertComment>>;
    getAlertsKpisByTenantId: (
        args: GetAlertsKpisByTenantIdArgs,
    ) => ApiResultResponse<MeterDataDto.GetAlertsKpisResponse>;
    getRegionsWithAlertsByTenantId: (
        args: GetRegionsWithAlertsByTenantIdArgs,
    ) => ApiResultResponse<Array<MeterDataDto.RegionOption>>;
    getSitesWithAlertsByTenantId: (
        args: GetSitesWithAlertsByTenantIdArgs,
    ) => ApiResultResponse<Array<MeterDataDto.SiteBrief>>;
    getMetersAvailableByTenantId: (
        args: GetAvailableMetersByTenantId,
    ) => ApiResultResponse<Array<MeterDataDto.BasicMeter>>;
    getIntervalMetersByTenantId: (
        args: GetIntervalMetersByTenantIdArgs,
    ) => ApiResultResponse<MeterDataDto.GetIntervalMetersResponse>;
    getIntervalMetersFiltersByTenantId: (
        args: GetIntervalMetersFiltersByTenantIdArgs,
    ) => ApiResultResponse<MeterDataDto.GetIntervalMetersFiltersResponse>;
    getMonthlyGridMetersByTenantId: (
        args: GetMonthlyGridMetersByTenantIdArgs,
    ) => ApiResultResponse<MeterDataDto.GetMonthlyGridMetersResponse>;
    getMonthlyGridMetersFiltersByTenantId: (
        args: GetMonthlyGridMetersFiltersByTenantIdArgs,
    ) => ApiResultResponse<MeterDataDto.GetMonthlyGridMetersFiltersResponse>;
    putSitesBySiteIdMetersByTenantId: (
        args: PutSitesBySiteIdMetersByTenantId,
    ) => ApiResultResponse<void>;
    getSiteBreakdownByTenantId: <T>(
        args: GetMeterDataByTenantIdSiteBreakdownArgs<T>,
    ) => ApiResultResponse<MeterDataDto.MeterDataSiteBreakdown>;
    getSitesActiveKpiByTenantId: (
        args: GetActiveSitesKpiByTenantIdArgs,
    ) => ApiResultResponse<MeterDataDto.ActiveSitesKpi>;
    getProvidersByTenantId: (
        args: GetProvidersByTenantIdArgs,
    ) => ApiResultResponse<Array<MeterDataDto.ProviderOption>>;
    getTotalConsumptionAndProductionKpiByTenantId: (
        args: GetTotalConsumptionAndProductionKpiByTenantIdArgs,
    ) => ApiResultResponse<MeterDataDto.TotalConsumptionAndProductionKpi>;
    getRegionsByTenantId: (
        args: GetRegionsByTenantIdArgs,
    ) => ApiResultResponse<Array<MeterDataDto.RegionOption>>;
    getBiddingZonesByTenantId: (
        args: GetBiddingZonesByTenantIdArgs,
    ) => ApiResultResponse<Array<MeterDataDto.BiddingZoneOption>>;
    getSitesByTenantId: (
        args: GetSitesByTenantIdArgs,
    ) => ApiResultResponse<Array<MeterDataDto.SiteOption>>;
    getHourlyAggregatedMeterDataByTenantId: (
        args: GetHourlyAggregatedMeterDataByTenantIdArgs,
    ) => ApiResultResponse<Array<MeterDataDto.HourlyAggregatedMeterData>>;
    getDataCompletenessByTenantId: (
        args: GetDataCompletenessByTenantIdArgs,
    ) => ApiResultResponse<MeterDataDto.DataCompleteness>;
    getAggregatedWarningsPerSiteByTenantId: (
        args: GetAggregatedWarningsPerSiteByTenantIdArgs,
    ) => ApiResultResponse<Array<MeterDataDto.AggregatedWarningsPerSite>>;
    getConsumptionBySiteByTenantId: (
        args: GetConsumptionBySiteByTenantIdArgs,
    ) => ApiResultResponse<MeterDataDto.ConsumptionBySite>;
    getWarningTypeOptionsByTenantId: (
        args: GetWarningTypeOptionsByTenantIdArgs,
    ) => ApiResultResponse<Array<SanityWarning>>;
    getMeterOptionsByTenantId: (
        args: GetMeterOptionsByTenantIdArgs,
    ) => ApiResultResponse<Array<MeterDataDto.MeterOption>>;
    getMonitoringSiteDetailsKpisByTenantId: (
        args: GetMonitoringSiteDetailsKpisByTenantIdArgs,
    ) => ApiResultResponse<MeterDataDto.MonitoringSiteDetailsKpis>;
    getHourlyMeterWarningsByTenantId: (
        args: GetHourlyMeterWarningsByTenantIdArgs,
    ) => ApiResultResponse<Array<MeterDataDto.SiteHourlyMeterWarnings>>;
    getHourlySiteWarningsByTenantId: (
        args: GetHourlySiteWarningsByTenantIdArgs,
    ) => ApiResultResponse<Array<MeterDataDto.SiteHourlySiteWarnings>>;
} & ApiClient<MeterDataPaths>;

export function getMeterDataClient(config: { useRetries?: boolean }): MeterDataClient;
export function getMeterDataClient(config: { useResult: boolean }): ResultMeterDataClient;
export function getMeterDataClient(config: {
    useResult?: boolean;
    useRetries?: boolean;
}): OverloadMeterDataClient {
    let url: string = "";

    let useMockData = false;
    if (typeof window !== "undefined") {
        url = window.location.origin;
    } else if (typeof process !== "undefined") {
        if (process.env.MOCK_METER_DATA == "true") {
            useMockData = true;
        }
        if (!process.env.METER_DATA_URL) {
            return crash("Missing METER_DATA_URL environment variable.");
        } else {
            url = process.env.METER_DATA_URL;
        }
    } else {
        return crash("No window or process, where the hell are we??!");
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    buildQueries({ ...config, useMockData: useMockData }, url);

    const { get, put, post, del } = buildQueries({ ...config, useMockData: useMockData }, url);

    return {
        deleteAlertsByAlertIdCommentsByCommentIdByTenantId: async ({
            getAccessToken,
            tenantId,
            alertId,
            commentId,
        }: DeleteAlertsByAlertIdCommentsByCommentIdByTenantIdArgs): OverloadApiResponse<void> => {
            const accessToken: string = await getAccessToken();

            return del({
                path: `/api/meter-data/${tenantId}/alerts/${alertId}/comments/${commentId}`,
                decoder: voidDecoder,
                headers: { Authorization: `Bearer ${accessToken}` },
            });
        },
        postAlertsByAlertIdCommentsByTenantId: async ({
            getAccessToken,
            tenantId,
            alertId,
            comment,
        }: PostAlertsByAlertIdCommentsByTenantIdArgs): OverloadApiResponse<MeterDataDto.AlertComment> => {
            const accessToken: string = await getAccessToken();

            return post({
                path: `/api/meter-data/${tenantId}/alerts/${alertId}/comments`,
                decoder: alertCommentDecoder,
                data: {
                    comment,
                },
                headers: { Authorization: `Bearer ${accessToken}` },
            });
        },
        postAlertsByAlertIdStatusByTenantId: async ({
            getAccessToken,
            tenantId,
            alertId,
            status,
        }: PostAlertsByAlertIdStatusByTenantIdArgs): OverloadApiResponse<void> => {
            const accessToken: string = await getAccessToken();

            return post({
                path: `/api/meter-data/${tenantId}/alerts/${alertId}/status`,
                decoder: voidDecoder,
                data: {
                    status,
                },
                headers: { Authorization: `Bearer ${accessToken}` },
            });
        },
        getAlertsByAlertIdByTenantId: async ({
            getAccessToken,
            tenantId,
            alertId,
        }: GetAlertsByAlertIdByTenantIdArgs): OverloadApiResponse<MeterDataDto.Alert> => {
            const accessToken: string = await getAccessToken();

            return get({
                path: `/api/meter-data/${tenantId}/alerts/${alertId}`,
                decoder: alertDecoder,
                headers: { Authorization: `Bearer ${accessToken}` },
            });
        },
        getAlertsByAlertIdCommentsByTenantId: async ({
            getAccessToken,
            tenantId,
            alertId,
        }: GetAlertsByAlertIdCommentsByTenantIdArgs): OverloadApiResponse<
            Array<MeterDataDto.AlertComment>
        > => {
            const accessToken: string = await getAccessToken();

            return get({
                path: `/api/meter-data/${tenantId}/alerts/${alertId}/comments`,
                decoder: alertCommentListDecoder,
                headers: { Authorization: `Bearer ${accessToken}` },
            });
        },
        getAlertsByTenantId: async ({
            getAccessToken,
            tenantId,
            ...params
        }: GetAlertsByTenantIdArgs): OverloadApiResponse<MeterDataDto.GetAlertsResponse> => {
            const accessToken: string = await getAccessToken();

            return get({
                path: `/api/meter-data/${tenantId}/alerts`,
                decoder: getAlertsResponseDecoder,
                params,
                headers: { Authorization: `Bearer ${accessToken}` },
            });
        },
        getAlertsKpisByTenantId: async ({
            getAccessToken,
            tenantId,
            ...params
        }: GetAlertsKpisByTenantIdArgs): OverloadApiResponse<MeterDataDto.GetAlertsKpisResponse> => {
            const accessToken: string = await getAccessToken();

            return get({
                path: `/api/meter-data/${tenantId}/alerts-kpis`,
                decoder: getAlertsKpisResponseDecoder,
                params,
                headers: { Authorization: `Bearer ${accessToken}` },
            });
        },
        getRegionsWithAlertsByTenantId: async ({
            getAccessToken,
            tenantId,
            ...params
        }: GetRegionsWithAlertsByTenantIdArgs): OverloadApiResponse<
            Array<MeterDataDto.RegionOption>
        > => {
            const accessToken: string = await getAccessToken();

            return get({
                path: `/api/meter-data/${tenantId}/regions-with-alerts`,
                decoder: regionOptionArrayDecoder,
                params: {
                    filters: {
                        status: params.filters?.status,
                        policies: params.filters?.policies,
                    },
                },
                headers: { Authorization: `Bearer ${accessToken}` },
            });
        },
        getSitesWithAlertsByTenantId: async ({
            getAccessToken,
            tenantId,
            ...params
        }: GetSitesWithAlertsByTenantIdArgs): OverloadApiResponse<
            Array<MeterDataDto.SiteBrief>
        > => {
            const accessToken: string = await getAccessToken();

            return get({
                path: `/api/meter-data/${tenantId}/sites-with-alerts`,
                decoder: siteBriefArrayDecoder,
                params: {
                    filters: {
                        status: params.filters?.status,
                        policies: params.filters?.policies,
                        regionIds: params.filters?.regionIds,
                    },
                },
                headers: { Authorization: `Bearer ${accessToken}` },
            });
        },
        getHourlyMeterWarningsByTenantId: async ({
            getAccessToken,
            tenantId,
            startTimeLocal,
            endTimeLocal,
            siteId,
        }: GetHourlyMeterWarningsByTenantIdArgs): OverloadApiResponse<
            Array<MeterDataDto.SiteHourlyMeterWarnings>
        > => {
            const accessToken: string = await getAccessToken();

            return get({
                path: `/api/meter-data/${tenantId}/hourly-meter-warnings`,
                decoder: hourlyMeterWarningsArrayDecoder,
                params: {
                    startTimeLocal: formatStartTimeLocal(startTimeLocal),
                    endTimeLocal: formatEndTimeLocal(endTimeLocal),
                    siteId,
                },
                headers: { Authorization: `Bearer ${accessToken}` },
            });
        },
        getHourlySiteWarningsByTenantId: async ({
            getAccessToken,
            tenantId,
            startTimeLocal,
            endTimeLocal,
            siteId,
        }: GetHourlySiteWarningsByTenantIdArgs): OverloadApiResponse<
            Array<MeterDataDto.SiteHourlySiteWarnings>
        > => {
            const accessToken: string = await getAccessToken();

            return get({
                path: `/api/meter-data/${tenantId}/hourly-site-warnings`,
                decoder: hourlySiteWarningsArrayDecoder,
                params: {
                    startTimeLocal: formatStartTimeLocal(startTimeLocal),
                    endTimeLocal: formatEndTimeLocal(endTimeLocal),
                    siteId,
                },
                headers: { Authorization: `Bearer ${accessToken}` },
            });
        },
        getMonitoringSiteDetailsKpisByTenantId: async ({
            getAccessToken,
            tenantId,
            startTimeLocal,
            endTimeLocal,
            siteId,
        }: GetMonitoringSiteDetailsKpisByTenantIdArgs): OverloadApiResponse<MeterDataDto.MonitoringSiteDetailsKpis> => {
            const accessToken: string = await getAccessToken();

            return get({
                path: `/api/meter-data/${tenantId}/monitoring-site-details-kpis`,
                decoder: monitoringSiteDetailsKpisDecoder,
                params: {
                    startTimeLocal: formatStartTimeLocal(startTimeLocal),
                    endTimeLocal: formatEndTimeLocal(endTimeLocal),
                    siteId,
                },
                headers: { Authorization: `Bearer ${accessToken}` },
            });
        },
        getMeterOptionsByTenantId: async ({
            getAccessToken,
            tenantId,
            siteId,
            startTimeLocal,
            endTimeLocal,
            warningType,
        }: GetMeterOptionsByTenantIdArgs): OverloadApiResponse<Array<MeterDataDto.MeterOption>> => {
            const accessToken: string = await getAccessToken();

            return get({
                path: `/api/meter-data/${tenantId}/meter-options`,
                decoder: meterOptionArrayDecoder,
                params: {
                    startTimeLocal: formatStartTimeLocal(startTimeLocal),
                    endTimeLocal: formatEndTimeLocal(endTimeLocal),
                    siteId,
                    ...(warningType && { warningType }),
                },
                headers: { Authorization: `Bearer ${accessToken}` },
            });
        },
        getWarningTypeOptionsByTenantId: async ({
            getAccessToken,
            tenantId,
            siteId,
            startTimeLocal,
            endTimeLocal,
            meterId,
        }: GetWarningTypeOptionsByTenantIdArgs): OverloadApiResponse<Array<SanityWarning>> => {
            const accessToken: string = await getAccessToken();

            return get({
                path: `/api/meter-data/${tenantId}/warning-type-options`,
                decoder: warningTypeArrayDecoder,
                params: {
                    startTimeLocal: formatStartTimeLocal(startTimeLocal),
                    endTimeLocal: formatEndTimeLocal(endTimeLocal),
                    siteId,
                    ...(meterId && { meterId }),
                },
                headers: { Authorization: `Bearer ${accessToken}` },
            });
        },
        getMetersAvailableByTenantId: async ({
            getAccessToken,
            tenantId,
            siteId,
        }: GetAvailableMetersByTenantId): OverloadApiResponse<Array<MeterDataDto.BasicMeter>> => {
            const accessToken: string = await getAccessToken();

            return get({
                path: `/api/meter-data/${tenantId}/meters/available`,
                decoder: basicMeterArrayDecoder,
                params: {
                    ...(siteId && { siteId }),
                },
                headers: { Authorization: `Bearer ${accessToken}` },
            });
        },
        getIntervalMetersByTenantId: async ({
            getAccessToken,
            tenantId,
            ...params
        }: GetIntervalMetersByTenantIdArgs): OverloadApiResponse<MeterDataDto.GetIntervalMetersResponse> => {
            const accessToken: string = await getAccessToken();

            return get({
                path: `/api/meter-data/${tenantId}/interval-meters`,
                decoder: getIntervalMetersResponseDecoder,
                params,
                headers: { Authorization: `Bearer ${accessToken}` },
            });
        },
        getIntervalMetersFiltersByTenantId: async ({
            getAccessToken,
            tenantId,
        }: GetIntervalMetersFiltersByTenantIdArgs): OverloadApiResponse<MeterDataDto.GetIntervalMetersFiltersResponse> => {
            const accessToken: string = await getAccessToken();

            return get({
                path: `/api/meter-data/${tenantId}/interval-meters-filters`,
                decoder: getIntervalMetersFiltersResponseDecoder,
                headers: { Authorization: `Bearer ${accessToken}` },
            });
        },
        getMonthlyGridMetersByTenantId: async ({
            getAccessToken,
            tenantId,
            ...params
        }: GetMonthlyGridMetersByTenantIdArgs): OverloadApiResponse<MeterDataDto.GetMonthlyGridMetersResponse> => {
            const accessToken: string = await getAccessToken();

            return get({
                path: `/api/meter-data/${tenantId}/monthly-grid-meters`,
                decoder: getMonthlyGridMetersResponseDecoder,
                params,
                headers: { Authorization: `Bearer ${accessToken}` },
            });
        },
        getMonthlyGridMetersFiltersByTenantId: async ({
            getAccessToken,
            tenantId,
        }: GetMonthlyGridMetersFiltersByTenantIdArgs): OverloadApiResponse<MeterDataDto.GetMonthlyGridMetersFiltersResponse> => {
            const accessToken: string = await getAccessToken();

            return get({
                path: `/api/meter-data/${tenantId}/monthly-grid-meters-filters`,
                decoder: getMonthlyGridMetersFiltersResponseDecoder,
                headers: { Authorization: `Bearer ${accessToken}` },
            });
        },
        putSitesBySiteIdMetersByTenantId: async ({
            getAccessToken,
            tenantId,
            siteId,
            meterIds,
        }: PutSitesBySiteIdMetersByTenantId): OverloadApiResponse<void> => {
            const accessToken: string = await getAccessToken();

            return put({
                path: `/api/meter-data/${tenantId}/sites/${siteId}/meters`,
                data: { meterIds },
                decoder: voidDecoder,
                headers: { Authorization: `Bearer ${accessToken}` },
            });
        },
        getSiteBreakdownByTenantId: async <T>({
            getAccessToken,
            tenantId,
            startTime,
            endTime,
            regionIds,
            biddingZoneIds,
            siteIds,
            page,
            pageSize,
            sorting,
            filters,
        }: GetMeterDataByTenantIdSiteBreakdownArgs<T>): OverloadApiResponse<MeterDataDto.MeterDataSiteBreakdown> => {
            const accessToken: string = await getAccessToken();

            return get({
                path: `/api/meter-data/${tenantId}/site-breakdown`,
                decoder: meterDataSiteBreakdownDecoder,
                params: {
                    startTimeLocal: formatStartTimeLocal(startTime),
                    endTimeLocal: formatEndTimeLocal(endTime),
                    ...(regionIds && { regionIds }),
                    ...(biddingZoneIds && { biddingZoneIds }),
                    ...(siteIds && { siteIds }),
                    page,
                    pageSize,
                    ...(sorting && {
                        orderBy: sorting.columnId,
                        orderDirection: sorting.direction,
                    }),
                    ...(filters && { filters }),
                },
                headers: { Authorization: `Bearer ${accessToken}` },
            });
        },
        getSitesActiveKpiByTenantId: async ({
            getAccessToken,
            tenantId,
            startTime,
            endTime,
            regionIds,
            biddingZoneIds,
            siteIds,
        }: GetActiveSitesKpiByTenantIdArgs): OverloadApiResponse<MeterDataDto.ActiveSitesKpi> => {
            const accessToken: string = await getAccessToken();

            return get({
                path: `/api/meter-data/${tenantId}/sites/active-kpi`,
                decoder: activeSitesKpiDecoder,
                params: {
                    startTimeLocal: formatStartTimeLocal(startTime),
                    endTimeLocal: formatEndTimeLocal(endTime),
                    ...(regionIds && { regionIds }),
                    ...(biddingZoneIds && { biddingZoneIds }),
                    ...(siteIds && { siteIds }),
                },
                headers: { Authorization: `Bearer ${accessToken}` },
            });
        },
        getTotalConsumptionAndProductionKpiByTenantId: async ({
            getAccessToken,
            tenantId,
            startTime,
            endTime,
            regionIds,
            biddingZoneIds,
            siteIds,
        }: GetTotalConsumptionAndProductionKpiByTenantIdArgs): OverloadApiResponse<MeterDataDto.TotalConsumptionAndProductionKpi> => {
            const accessToken: string = await getAccessToken();

            return get({
                path: `/api/meter-data/${tenantId}/total-consumption-and-production-kpi`,
                decoder: totalConsumptionAndProductionKpiDecoder,
                params: {
                    startTimeLocal: formatStartTimeLocal(startTime),
                    endTimeLocal: formatEndTimeLocal(endTime),
                    ...(regionIds && { regionIds }),
                    ...(biddingZoneIds && { biddingZoneIds }),
                    ...(siteIds && { siteIds }),
                },
                headers: { Authorization: `Bearer ${accessToken}` },
            });
        },
        getHourlyAggregatedMeterDataByTenantId: async ({
            getAccessToken,
            tenantId,
            startTime,
            endTime,
            regionIds,
            biddingZoneIds,
            siteIds,
            granularity,
        }: GetHourlyAggregatedMeterDataByTenantIdArgs): OverloadApiResponse<
            Array<MeterDataDto.HourlyAggregatedMeterData>
        > => {
            const accessToken: string = await getAccessToken();

            return get({
                path: `/api/meter-data/${tenantId}/hourly-aggregated-meter-data`,
                decoder: hourlyAggregatedMeterDataArrayDecoder,
                params: {
                    startTimeLocal: formatStartTimeLocal(startTime),
                    endTimeLocal: formatEndTimeLocal(endTime),
                    ...(regionIds && { regionIds }),
                    ...(biddingZoneIds && { biddingZoneIds }),
                    ...(siteIds && { siteIds }),
                    ...(granularity && { granularity }),
                },
                headers: { Authorization: `Bearer ${accessToken}` },
            });
        },
        getDataCompletenessByTenantId: async ({
            getAccessToken,
            tenantId,
            startTime,
            endTime,
            regionIds,
            countryIds,
            biddingZoneIds,
            siteIds,
        }: GetDataCompletenessByTenantIdArgs): OverloadApiResponse<MeterDataDto.DataCompleteness> => {
            const accessToken: string = await getAccessToken();

            return get({
                path: `/api/meter-data/${tenantId}/data-completeness`,
                decoder: dataCompletenessDecoder,
                params: {
                    startTimeLocal: formatStartTimeLocal(startTime),
                    endTimeLocal: formatEndTimeLocal(endTime),
                    ...(regionIds && { regionIds }),
                    ...(countryIds && { countryIds }),
                    ...(biddingZoneIds && { biddingZoneIds }),
                    ...(siteIds && { siteIds }),
                },
                headers: { Authorization: `Bearer ${accessToken}` },
                timeoutMs: 2 * ONE_MINUTE_MS,
            });
        },
        getAggregatedWarningsPerSiteByTenantId: async ({
            getAccessToken,
            tenantId,
            startTime,
            endTime,
            siteIds,
        }: GetAggregatedWarningsPerSiteByTenantIdArgs): OverloadApiResponse<
            Array<MeterDataDto.AggregatedWarningsPerSite>
        > => {
            const accessToken: string = await getAccessToken();

            return get({
                path: `/api/meter-data/${tenantId}/aggregated-warnings-per-site`,
                decoder: hourlyAggregatedWarningsPerSiteArrayDecoder,
                params: {
                    startTimeLocal: formatStartTimeLocal(startTime),
                    endTimeLocal: formatEndTimeLocal(endTime),
                    siteIds,
                },
                headers: { Authorization: `Bearer ${accessToken}` },
            });
        },
        getRegionsByTenantId: async ({
            getAccessToken,
            tenantId,
            startTime,
            endTime,
        }: GetRegionsByTenantIdArgs): OverloadApiResponse<Array<MeterDataDto.RegionOption>> => {
            const accessToken: string = await getAccessToken();

            return get({
                path: `/api/meter-data/${tenantId}/regions`,
                decoder: regionOptionArrayDecoder,
                params: {
                    startTimeLocal: formatStartTimeLocal(startTime),
                    endTimeLocal: formatEndTimeLocal(endTime),
                },
                headers: { Authorization: `Bearer ${accessToken}` },
            });
        },
        getBiddingZonesByTenantId: async ({
            getAccessToken,
            tenantId,
            startTime,
            endTime,
            regionIds,
        }: GetBiddingZonesByTenantIdArgs): OverloadApiResponse<
            Array<MeterDataDto.BiddingZoneOption>
        > => {
            const accessToken: string = await getAccessToken();

            return get({
                path: `/api/meter-data/${tenantId}/bidding-zones`,
                decoder: biddingZoneOptionArrayDecoder,
                params: {
                    startTimeLocal: formatStartTimeLocal(startTime),
                    endTimeLocal: formatEndTimeLocal(endTime),
                    ...(regionIds && { regionIds }),
                },
                headers: { Authorization: `Bearer ${accessToken}` },
            });
        },
        getSitesByTenantId: async ({
            getAccessToken,
            tenantId,
            startTime,
            endTime,
            regionIds,
            biddingZoneIds,
        }: GetSitesByTenantIdArgs): OverloadApiResponse<Array<MeterDataDto.SiteOption>> => {
            const accessToken: string = await getAccessToken();

            return get({
                path: `/api/meter-data/${tenantId}/sites`,
                decoder: siteOptionArrayDecoder,
                params: {
                    startTimeLocal: formatStartTimeLocal(startTime),
                    endTimeLocal: formatEndTimeLocal(endTime),
                    ...(regionIds && { regionIds }),
                    ...(biddingZoneIds && { biddingZoneIds }),
                },
                headers: { Authorization: `Bearer ${accessToken}` },
            });
        },
        getProvidersByTenantId: async ({
            getAccessToken,
            tenantId,
            startTime,
            endTime,
            regionIds,
            biddingZoneIds,
            siteIds,
        }: GetProvidersByTenantIdArgs): OverloadApiResponse<Array<MeterDataDto.ProviderOption>> => {
            const accessToken: string = await getAccessToken();

            return get({
                path: `/api/meter-data/${tenantId}/providers`,
                decoder: providerOptionArrayDecoder,
                params: {
                    startTimeLocal: formatStartTimeLocal(startTime),
                    endTimeLocal: formatEndTimeLocal(endTime),
                    ...(regionIds && { regionIds }),
                    ...(biddingZoneIds && { biddingZoneIds }),
                    ...(siteIds && { siteIds }),
                },
                headers: { Authorization: `Bearer ${accessToken}` },
            });
        },
        getConsumptionBySiteByTenantId: async ({
            getAccessToken,
            tenantId,
            startTimeLocal,
            endTimeLocal,
            regionIds,
            consumptionSiteGroupIds,
            page,
            orderBy,
            orderDir,
            readingTypes,
        }: GetConsumptionBySiteByTenantIdArgs): OverloadApiResponse<MeterDataDto.ConsumptionBySite> => {
            const accessToken = await getAccessToken();
            return get({
                path: `/api/meter-data/${tenantId}/consumption-by-site`,
                decoder: consumptionBySiteDecoder,
                params: {
                    startTimeLocal: getLocalStartOfDayISOString(startTimeLocal),
                    endTimeLocal: getLocalStartOfDayISOString(endTimeLocal),
                    ...(orderBy && { orderBy }),
                    ...(regionIds && { regionIds }),
                    ...(orderDir && { orderDir }),
                    ...(consumptionSiteGroupIds && { consumptionSiteGroupIds }),
                    ...(readingTypes && { readingTypes }),
                    page,
                },
                headers: { Authorization: `Bearer ${accessToken}` },
            });
        },
        getInvoicedGridConsumptionByDataSubTypeKpisByTenantId: async ({
            getAccessToken,
            tenantId,
            dataSubType,
            startTimeLocal,
            endTimeLocal,
            dataCompleteness,
            regionIds,
            siteIds,
        }: GetInvoicedGridConsumptionByDataSubTypeKpisByTenantIdArgs): OverloadApiResponse<MeterDataDto.GridConsumptionKpis> => {
            const accessToken = await getAccessToken();
            return get({
                path: `/api/meter-data/${tenantId}/invoiced-grid-consumption/${dataSubType}/kpis`,
                decoder: gridConsumptionKpisDecoder,
                params: {
                    startTimeLocal: getLocalStartOfDayISOString(startTimeLocal),
                    endTimeLocal: getLocalStartOfDayISOString(endTimeLocal),
                    ...(dataCompleteness && { dataCompleteness }),
                    ...(regionIds && { regionIds }),
                    ...(siteIds && { siteIds }),
                },
                headers: { Authorization: `Bearer ${accessToken}` },
            });
        },
        getInvoicedGridConsumptionByDataSubTypeMonthlyBreakdownByTenantId: async ({
            getAccessToken,
            tenantId,
            dataSubType,
            startTimeLocal,
            endTimeLocal,
            dataCompleteness,
            regionIds,
            siteIds,
        }: GetInvoicedGridConsumptionByDataSubTypeMonthlyBreakdownByTenantIdArgs): OverloadApiResponse<MeterDataDto.GridConsumptionMonthlyBreakdown> => {
            const accessToken = await getAccessToken();
            return get({
                path: `/api/meter-data/${tenantId}/invoiced-grid-consumption/${dataSubType}/monthly-breakdown`,
                decoder: gridConsumptionMonthlyBreakdownDecoder,
                params: {
                    startTimeLocal: getLocalStartOfDayISOString(startTimeLocal),
                    endTimeLocal: getLocalStartOfDayISOString(endTimeLocal),
                    ...(dataCompleteness && { dataCompleteness }),
                    ...(regionIds && { regionIds }),
                    ...(siteIds && { siteIds }),
                },
                headers: { Authorization: `Bearer ${accessToken}` },
            });
        },
        getInvoicedGridConsumptionFiltersActiveRegionsByTenantId: async ({
            getAccessToken,
            tenantId,
            startTimeLocal,
            endTimeLocal,
            dataCompleteness,
        }: GetInvoicedGridConsumptionFiltersActiveRegionsByTenantIdArgs): OverloadApiResponse<
            Array<MeterDataDto.RegionOption>
        > => {
            const accessToken = await getAccessToken();
            return get({
                path: `/api/meter-data/${tenantId}/invoiced-grid-consumption/filters/active-regions`,
                decoder: regionOptionArrayDecoder,
                params: {
                    startTimeLocal: getLocalStartOfDayISOString(startTimeLocal),
                    endTimeLocal: getLocalStartOfDayISOString(endTimeLocal),
                    ...(dataCompleteness && { dataCompleteness }),
                },
                headers: { Authorization: `Bearer ${accessToken}` },
            });
        },
        getInvoicedGridConsumptionFiltersActiveSitesByTenantId: async ({
            getAccessToken,
            tenantId,
            startTimeLocal,
            endTimeLocal,
            dataCompleteness,
            regionIds,
        }: GetInvoicedGridConsumptionFiltersActiveSitesByTenantIdArgs): OverloadApiResponse<
            Array<MeterDataDto.SiteBrief>
        > => {
            const accessToken = await getAccessToken();
            return get({
                path: `/api/meter-data/${tenantId}/invoiced-grid-consumption/filters/active-sites`,
                decoder: siteBriefArrayDecoder,
                params: {
                    startTimeLocal: getLocalStartOfDayISOString(startTimeLocal),
                    endTimeLocal: getLocalStartOfDayISOString(endTimeLocal),
                    ...(dataCompleteness && { dataCompleteness }),
                    ...(regionIds && { regionIds }),
                },
                headers: { Authorization: `Bearer ${accessToken}` },
            });
        },
        getInvoicedGridConsumptionByDataSubTypeComparisonByTenantId: async ({
            getAccessToken,
            tenantId,
            startTimeLocal,
            endTimeLocal,
            dataCompleteness,
            regionIds,
            siteIds,
            dataSubType,
        }: GetInvoicedGridConsumptionByDataSubTypeComparisonByTenantIdArgs): OverloadApiResponse<MeterDataDto.GridConsumptionComparison> => {
            const accessToken = await getAccessToken();
            return get({
                path: `/api/meter-data/${tenantId}/invoiced-grid-consumption/${dataSubType}/comparison`,
                decoder: gridConsumptionComparisonDecoder,
                params: {
                    startTimeLocal: getLocalStartOfDayISOString(startTimeLocal),
                    endTimeLocal: getLocalStartOfDayISOString(endTimeLocal),
                    ...(dataCompleteness && { dataCompleteness }),
                    ...(regionIds && { regionIds }),
                    ...(siteIds && { siteIds }),
                },
                headers: { Authorization: `Bearer ${accessToken}` },
            });
        },
    };
}
